import React, { useEffect, useRef, useState } from 'react';
import { getDocument, GlobalWorkerOptions, OPS, type PdfJsDocument, type PdfJsPage } from 'pdfjs-dist/build/pdf';
import {
  applyContractPdfEdits,
  assignContractWrapWidths,
  contractFontFamily,
  contractFontIsBold,
  layoutContractBaselines,
  prepareContractRuns,
  ruleInsideBox,
  textPiecesToRuns,
  type ContractPdfBox,
  type ContractPdfEditModel,
  type ContractPdfFontFamily,
  type ContractPdfRule,
  type ContractSignatureLine,
  type ContractTextRun,
  type ExtractedTextPiece,
} from '../utils/contractPdfEdits';
import './ContractPdfEditor.css';

GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.js`;

type PageSize = { width: number; height: number };
type Active = { kind: 'text' | 'signature'; id: string } | null;
type Drag =
  | { kind: 'text'; id: string; pageIndex: number; dx: number; dy: number }
  | { kind: 'signature'; id: string; pageIndex: number; dx: number; dy: number }
  | { kind: 'resize'; id: string; pageIndex: number };

const DRAW_ERROR = 'Could not draw the contract pages.';

/** A new scale or a closed editor cancels the paint. That is not a broken PDF. */
function isCancelledRender(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false;
  const name = String((err as { name?: string }).name || '');
  const message = String((err as { message?: string }).message || '');
  return name === 'RenderingCancelledException' || /rendering cancelled/i.test(message) || /same canvas/i.test(message);
}

function fontStack(family: ContractPdfFontFamily): string {
  if (family === 'times') return 'Times New Roman, Times, serif';
  if (family === 'courier') return '"Courier New", Courier, monospace';
  return 'Arial, Helvetica, sans-serif';
}

let measureCanvas: HTMLCanvasElement | null = null;

function measureWidth(sample: string, fontSize: number, bold: boolean, family: ContractPdfFontFamily): number {
  const canvas = measureCanvas || (measureCanvas = document.createElement('canvas'));
  const ctx = canvas.getContext('2d');
  if (!ctx) return sample.length * fontSize * 0.5;
  ctx.font = `${bold ? '700 ' : ''}${fontSize}px ${fontStack(family)}`;
  return ctx.measureText(sample).width;
}

const baselineOffsets = new Map<string, number>();

/**
 * Distance in CSS px from the top of a line box to its baseline. Browsers round
 * font metrics and snap to device pixels, so this is measured instead of
 * derived from the font's ascent.
 */
function cssBaselineOffset(fontFamily: string, bold: boolean, fontSize: number, lineHeight: number): number {
  const key = `${fontFamily}|${bold ? 700 : 400}|${fontSize.toFixed(2)}|${lineHeight.toFixed(2)}|${window.devicePixelRatio}`;
  const known = baselineOffsets.get(key);
  if (known !== undefined) return known;
  const line = document.createElement('div');
  line.style.cssText = 'position:absolute;left:0;top:0;visibility:hidden;white-space:pre;margin:0;padding:0;border:0';
  line.style.fontFamily = fontFamily;
  line.style.fontWeight = bold ? '700' : '400';
  line.style.fontSize = `${fontSize}px`;
  line.style.lineHeight = `${lineHeight}px`;
  const marker = document.createElement('span');
  marker.style.cssText = 'display:inline-block;width:0;height:0';
  line.append(marker, 'Hg');
  document.body.appendChild(line);
  const measured = marker.getBoundingClientRect().top - line.getBoundingClientRect().top;
  line.remove();
  const offset = measured > 0 ? measured : lineHeight / 2 + fontSize * 0.35;
  baselineOffsets.set(key, offset);
  return offset;
}

function readBlockText(node: HTMLElement): string {
  return (node.innerText ?? '')
    .replace(/\u00a0/g, ' ')
    .replace(/\r/g, '')
    .replace(/[^\S\n]+/g, ' ')
    .replace(/[ \t]*\n[ \t]*/g, '\n');
}

function insertAtCaret(node: HTMLElement, value: string) {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) return;
  const range = selection.getRangeAt(0);
  range.deleteContents();
  const textNode = document.createTextNode(value);
  range.insertNode(textNode);
  range.setStartAfter(textNode);
  range.collapse(true);
  selection.removeAllRanges();
  selection.addRange(range);
  node.normalize();
}

function newId(): string {
  return `c-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
}

function cloneModel(model: ContractPdfEditModel): ContractPdfEditModel {
  return {
    runs: model.runs.map((run) => ({
      ...run,
      erase: run.erase ? { ...run.erase } : null,
      origin: run.origin ? { ...run.origin } : null,
    })),
    signatures: model.signatures.map((line) => ({ ...line })),
    erasures: model.erasures.map((box) => ({ ...box })),
  };
}

async function fontMeta(page: PdfJsPage, fontName: string): Promise<{ bold: boolean; family: ReturnType<typeof contractFontFamily> }> {
  for (let attempt = 0; attempt < 15; attempt++) {
    if (page.commonObjs.has(fontName)) {
      const font = page.commonObjs.get(fontName);
      const name = `${font.name || ''} ${font.fallbackName || ''}`;
      return { bold: contractFontIsBold(name, font.bold), family: contractFontFamily(name) };
    }
    await new Promise((resolve) => setTimeout(resolve, 40));
  }
  return { bold: false, family: 'helvetica' };
}

type RawPiece = ExtractedTextPiece & { fontName: string };

/** If the PDF font name did not say bold, the wider of the two families is the bold face. */
function inferBoldFromWidth(pieces: RawPiece[]) {
  if (pieces.some((piece) => piece.bold)) return;
  const stats = new Map<string, { width: number; units: number }>();
  for (const piece of pieces) {
    const units = piece.str.length * piece.fontSize;
    if (units <= 0) continue;
    const stat = stats.get(piece.fontName) ?? { width: 0, units: 0 };
    stat.width += piece.width;
    stat.units += units;
    stats.set(piece.fontName, stat);
  }
  let widest = '';
  let widestAvg = 0;
  let narrowest = Infinity;
  stats.forEach((stat, name) => {
    const avg = stat.width / stat.units;
    if (avg > widestAvg) {
      widestAvg = avg;
      widest = name;
    }
    if (avg < narrowest) narrowest = avg;
  });
  if (!widest || !Number.isFinite(narrowest) || widestAvg < narrowest * 1.03) return;
  for (const piece of pieces) {
    if (piece.fontName === widest) piece.bold = true;
  }
}

async function piecesFromPage(page: PdfJsPage): Promise<RawPiece[]> {
  const scratch = document.createElement('canvas');
  const viewport = page.getViewport({ scale: 1 });
  scratch.width = Math.ceil(viewport.width);
  scratch.height = Math.ceil(viewport.height);
  const ctx = scratch.getContext('2d');
  if (ctx) await page.render({ canvasContext: ctx, viewport }).promise;
  const content = await page.getTextContent();
  const cache = new Map<string, { bold: boolean; family: ReturnType<typeof contractFontFamily> }>();
  const pieces: RawPiece[] = [];
  for (const item of content.items) {
    if (!('str' in item) || !item.str.trim()) continue;
    const transform = item.transform;
    const fontSize = Math.hypot(transform[2] || 0, transform[3] || 0) || Math.abs(transform[0] || 0) || 10;
    const angle = Math.atan2(transform[1] || 0, transform[0] || 1);
    if (Math.abs(angle) > 0.08) continue;
    let meta = cache.get(item.fontName);
    if (!meta) {
      meta = await fontMeta(page, item.fontName);
      cache.set(item.fontName, meta);
    }
    pieces.push({
      str: item.str,
      x: transform[4] || 0,
      baseline: transform[5] || 0,
      width: item.width || 0,
      fontSize,
      bold: meta.bold,
      font: meta.family,
      fontName: item.fontName,
    });
  }
  return pieces;
}

type Matrix = [number, number, number, number, number, number];
type Point = [number, number];
type PendingPath = { ops: ArrayLike<number>; coords: ArrayLike<number>; ctm: Matrix };

const STROKE_OPS = new Set([
  OPS.stroke,
  OPS.closeStroke,
  OPS.fillStroke,
  OPS.eoFillStroke,
  OPS.closeFillStroke,
  OPS.closeEOFillStroke,
]);
const CLOSING_STROKE_OPS = new Set([OPS.closeStroke, OPS.closeFillStroke, OPS.closeEOFillStroke]);
const PATH_DISCARD_OPS = new Set([OPS.fill, OPS.eoFill, OPS.endPath]);

function concatMatrix(m: Matrix, n: ArrayLike<number>): Matrix {
  return [
    m[0] * n[0] + m[2] * n[1],
    m[1] * n[0] + m[3] * n[1],
    m[0] * n[2] + m[2] * n[3],
    m[1] * n[2] + m[3] * n[3],
    m[0] * n[4] + m[2] * n[5] + m[4],
    m[1] * n[4] + m[3] * n[5] + m[5],
  ];
}

/** Straight pieces of one path, in page points. Curves only move the pen. */
function pathSegments(path: PendingPath, closeLast: boolean): Array<[Point, Point]> {
  const { ops, coords, ctm } = path;
  const point = (x: number, y: number): Point => [ctm[0] * x + ctm[2] * y + ctm[4], ctm[1] * x + ctm[3] * y + ctm[5]];
  const segments: Array<[Point, Point]> = [];
  let start: Point | null = null;
  let pen: Point | null = null;
  const lineTo = (next: Point) => {
    if (pen) segments.push([pen, next]);
    pen = next;
  };
  let k = 0;
  for (let i = 0; i < ops.length; i++) {
    const op = ops[i];
    if (op === OPS.moveTo) {
      start = pen = point(coords[k], coords[k + 1]);
      k += 2;
    } else if (op === OPS.lineTo) {
      lineTo(point(coords[k], coords[k + 1]));
      k += 2;
    } else if (op === OPS.curveTo) {
      pen = point(coords[k + 4], coords[k + 5]);
      k += 6;
    } else if (op === OPS.curveTo2 || op === OPS.curveTo3) {
      pen = point(coords[k + 2], coords[k + 3]);
      k += 4;
    } else if (op === OPS.rectangle) {
      const [x, y, w, h] = [coords[k], coords[k + 1], coords[k + 2], coords[k + 3]];
      k += 4;
      start = pen = point(x, y);
      lineTo(point(x + w, y));
      lineTo(point(x + w, y + h));
      lineTo(point(x, y + h));
      lineTo(start);
    } else if (op === OPS.closePath && start) {
      lineTo(start);
    }
  }
  if (closeLast && start) lineTo(start);
  return segments;
}

type Graphics = { ctm: Matrix; lineWidth: number; color: [number, number, number]; dashed: boolean };

/** The horizontal and vertical pieces of a stroked path. Diagonals and curves are not rules. */
function strokedRules(paths: PendingPath[], closing: boolean, state: Graphics, pageIndex: number): ContractPdfRule[] {
  const { ctm, lineWidth, color } = state;
  const width = lineWidth * Math.sqrt(Math.abs(ctm[0] * ctm[3] - ctm[1] * ctm[2]));
  const rules: ContractPdfRule[] = [];
  paths.forEach((path, index) => {
    for (const [a, b] of pathSegments(path, closing && index === paths.length - 1)) {
      const dx = Math.abs(b[0] - a[0]);
      const dy = Math.abs(b[1] - a[1]);
      if (Math.max(dx, dy) < 0.5) continue;
      if (dy <= 0.05) {
        const y = (a[1] + b[1]) / 2;
        rules.push({ pageIndex, x1: Math.min(a[0], b[0]), y1: y, x2: Math.max(a[0], b[0]), y2: y, width, color });
      } else if (dx <= 0.05) {
        const x = (a[0] + b[0]) / 2;
        rules.push({ pageIndex, x1: x, y1: Math.min(a[1], b[1]), x2: x, y2: Math.max(a[1], b[1]), width, color });
      }
    }
  });
  return rules;
}

/** Underlines and table borders on one page, so erasing the text over them can leave them whole. */
async function rulesFromPage(page: PdfJsPage, pageIndex: number): Promise<ContractPdfRule[]> {
  const { fnArray, argsArray } = await page.getOperatorList();
  let state: Graphics = { ctm: [1, 0, 0, 1, 0, 0], lineWidth: 1, color: [0, 0, 0], dashed: false };
  const saved: Graphics[] = [];
  let pending: PendingPath[] = [];
  const rules: ContractPdfRule[] = [];
  for (let i = 0; i < fnArray.length; i++) {
    const fn = fnArray[i];
    const args = (argsArray[i] ?? []) as ArrayLike<unknown>;
    if (fn === OPS.save) saved.push(state);
    else if (fn === OPS.restore || fn === OPS.paintFormXObjectEnd) state = saved.pop() ?? state;
    else if (fn === OPS.transform) state = { ...state, ctm: concatMatrix(state.ctm, args as ArrayLike<number>) };
    else if (fn === OPS.paintFormXObjectBegin) {
      saved.push(state);
      const matrix = args[0] as ArrayLike<number> | null | undefined;
      if (matrix && matrix.length === 6) state = { ...state, ctm: concatMatrix(state.ctm, matrix) };
    } else if (fn === OPS.setLineWidth) state = { ...state, lineWidth: Number(args[0]) || 0 };
    else if (fn === OPS.setDash) {
      const dash = args[0] as ArrayLike<number> | undefined;
      state = { ...state, dashed: Boolean(dash && dash.length > 0) };
    } else if (fn === OPS.setStrokeRGBColor) {
      const [r, g, b] = Array.from(args as ArrayLike<number>);
      state = { ...state, color: [r / 255, g / 255, b / 255] };
    } else if (fn === OPS.constructPath) {
      pending.push({ ops: args[0] as ArrayLike<number>, coords: args[1] as ArrayLike<number>, ctm: state.ctm });
    } else if (STROKE_OPS.has(fn)) {
      if (!state.dashed) rules.push(...strokedRules(pending, CLOSING_STROKE_OPS.has(fn), state, pageIndex));
      pending = [];
    } else if (PATH_DISCARD_OPS.has(fn)) pending = [];
  }
  return rules;
}

/** The white masks hide the original glyphs and the underlines beneath them. Stroke those pieces back. */
function strokeRulesUnderMasks(
  ctx: CanvasRenderingContext2D,
  rules: ContractPdfRule[],
  masks: ContractPdfBox[],
  pageHeight: number,
  scale: number
) {
  ctx.save();
  ctx.lineCap = 'butt';
  for (const mask of masks) {
    for (const rule of rules) {
      const piece = ruleInsideBox(rule, mask, 1 / scale);
      if (!piece) continue;
      const [r, g, b] = rule.color.map((channel) => Math.round(channel * 255));
      ctx.strokeStyle = `rgb(${r}, ${g}, ${b})`;
      ctx.lineWidth = Math.max(1, rule.width * scale);
      ctx.beginPath();
      ctx.moveTo(piece.x1 * scale, (pageHeight - piece.y1) * scale);
      ctx.lineTo(piece.x2 * scale, (pageHeight - piece.y2) * scale);
      ctx.stroke();
    }
  }
  ctx.restore();
}

/**
 * In-place editor for a generated contract. Existing lines can be typed in and
 * dragged. Save writes the result into the PDF that is attached to the email.
 */
const ContractPdfEditor: React.FC<{
  pdfBytes: Uint8Array;
  initialModel?: ContractPdfEditModel | null;
  onCancel: () => void;
  onSave: (bytes: Uint8Array, model: ContractPdfEditModel) => void;
}> = ({ pdfBytes, initialModel, onCancel, onSave }) => {
  const [pages, setPages] = useState<PageSize[]>([]);
  const [runs, setRuns] = useState<ContractTextRun[]>([]);
  const [signatures, setSignatures] = useState<ContractSignatureLine[]>([]);
  const [erasures, setErasures] = useState<ContractPdfEditModel['erasures']>([]);
  const [masks, setMasks] = useState<Array<ContractPdfBox & { pageIndex: number }>>([]);
  const [rules, setRules] = useState<ContractPdfRule[]>([]);
  const [active, setActive] = useState<Active>(null);
  const [placingSignature, setPlacingSignature] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [viewScale, setViewScale] = useState(1.35);
  const docRef = useRef<PdfJsDocument | null>(null);
  const pagesScrollRef = useRef<HTMLDivElement | null>(null);
  const canvasRefs = useRef<Array<HTMLCanvasElement | null>>([]);
  const surfaceRefs = useRef<Array<HTMLDivElement | null>>([]);
  const dragRef = useRef<Drag | null>(null);
  const runsRef = useRef(runs);
  const signaturesRef = useRef(signatures);
  const erasuresRef = useRef(erasures);
  runsRef.current = runs;
  signaturesRef.current = signatures;
  erasuresRef.current = erasures;
  const updateRuns = (next: ContractTextRun[] | ((prev: ContractTextRun[]) => ContractTextRun[])) => {
    const resolved = typeof next === 'function' ? next(runsRef.current) : next;
    runsRef.current = resolved;
    setRuns(resolved);
  };
  const updateSignatures = (
    next: ContractSignatureLine[] | ((prev: ContractSignatureLine[]) => ContractSignatureLine[])
  ) => {
    const resolved = typeof next === 'function' ? next(signaturesRef.current) : next;
    signaturesRef.current = resolved;
    setSignatures(resolved);
  };
  const placements = React.useMemo(
    () => layoutContractBaselines(runs, (run, sample) => measureWidth(sample, run.fontSize, run.bold, run.font)),
    [runs]
  );
  const shownBaselineRef = useRef<Record<string, number>>({});
  const shownBaselines: Record<string, number> = {};
  for (const placement of placements) shownBaselines[placement.id] = placement.baseline;
  shownBaselineRef.current = shownBaselines;

  useEffect(() => {
    let cancelled = false;
    const loadingTask = getDocument({
      data: pdfBytes.slice(0),
      standardFontDataUrl: `${process.env.PUBLIC_URL}/pdf-standard-fonts/`,
    });
    loadingTask.promise
      .then(async (doc) => {
        if (cancelled) {
          doc.destroy();
          return;
        }
        docRef.current = doc;
        const sizes: PageSize[] = [];
        const nextRules: ContractPdfRule[] = [];
        for (let i = 1; i <= doc.numPages; i++) {
          const page = await doc.getPage(i);
          const viewport = page.getViewport({ scale: 1 });
          sizes.push({ width: viewport.width, height: viewport.height });
          // Without the rules the editor still works; erased text just leaves gaps in its underline.
          nextRules.push(...(await rulesFromPage(page, i - 1).catch(() => [])));
        }
        let nextRuns: ContractTextRun[] = [];
        let nextSignatures: ContractSignatureLine[] = [];
        let nextErasures: ContractPdfEditModel['erasures'] = [];
        const pageWidths = sizes.map((size) => size.width);
        if (initialModel) {
          const copy = cloneModel(initialModel);
          nextRuns = assignContractWrapWidths(
            copy.runs.map((run) => ({
              ...run,
              maxWidth: run.maxWidth || 0,
              lineHeight: run.lineHeight || run.fontSize * 1.2,
            })),
            pageWidths
          );
          nextSignatures = copy.signatures;
          nextErasures = copy.erasures;
        } else {
          const collected: RawPiece[][] = [];
          for (let i = 0; i < sizes.length; i++) {
            if (cancelled) return;
            const page = await doc.getPage(i + 1);
            collected.push(await piecesFromPage(page));
          }
          inferBoldFromWidth(collected.flat());
          collected.forEach((pieces, pageIndex) => {
            nextRuns.push(...textPiecesToRuns(pageIndex, pieces, newId));
          });
          nextRuns = prepareContractRuns(nextRuns, pageWidths);
        }
        const nextMasks = [
          ...nextRuns.flatMap((run) => (run.erase ? [{ pageIndex: run.pageIndex, ...run.erase }] : [])),
          ...nextErasures,
        ];
        if (cancelled) return;
        setPages(sizes);
        updateRuns(nextRuns);
        updateSignatures(nextSignatures);
        setErasures(nextErasures);
        setMasks(nextMasks);
        setRules(nextRules);
        setLoading(false);
      })
      .catch(() => {
        if (!cancelled) {
          setError('Could not open this PDF for editing.');
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
      docRef.current?.destroy();
      docRef.current = null;
    };
  }, [pdfBytes, initialModel]);

  useEffect(() => {
    const doc = docRef.current;
    if (!doc || pages.length === 0) return;
    let cancelled = false;
    let active: { cancel: () => void } | null = null;
    (async () => {
      try {
        for (let i = 0; i < pages.length; i++) {
          if (cancelled) return;
          const canvas = canvasRefs.current[i];
          if (!canvas) continue;
          const page = await doc.getPage(i + 1);
          if (cancelled) return;
          const viewport = page.getViewport({ scale: viewScale });
          const ctx = canvas.getContext('2d');
          if (!ctx) continue;
          canvas.width = viewport.width;
          canvas.height = viewport.height;
          const task = page.render({ canvasContext: ctx, viewport });
          active = task;
          try {
            await task.promise;
          } catch (err) {
            if (cancelled || isCancelledRender(err)) return;
            throw err;
          }
          if (active === task) active = null;
          if (cancelled) return;
          ctx.fillStyle = '#ffffff';
          const pageMasks: ContractPdfBox[] = [];
          for (const mask of masks) {
            if (mask.pageIndex !== i) continue;
            const x = mask.x * viewScale;
            const y = (pages[i].height - mask.y - mask.height) * viewScale;
            ctx.fillRect(x, y, mask.width * viewScale, mask.height * viewScale);
            pageMasks.push(mask);
          }
          const pageRules = rules.filter((rule) => rule.pageIndex === i);
          strokeRulesUnderMasks(ctx, pageRules, pageMasks, pages[i].height, viewScale);
        }
        if (!cancelled) setError((current) => (current === DRAW_ERROR ? '' : current));
      } catch (err) {
        if (!cancelled && !isCancelledRender(err)) {
          console.error(DRAW_ERROR, err);
          setError(DRAW_ERROR);
        }
      }
    })();
    return () => {
      cancelled = true;
      active?.cancel();
    };
  }, [pages, masks, rules, viewScale]);

  useEffect(() => {
    const el = pagesScrollRef.current;
    if (!el || pages.length === 0) return;
    const fit = () => {
      const pageWidth = pages[0]?.width || 612;
      const next = (el.clientWidth - 24) / pageWidth;
      const rounded = Math.min(1.9, Math.max(1.05, Math.round(next * 20) / 20));
      setViewScale((current) => (Math.abs(current - rounded) < 0.02 ? current : rounded));
    };
    fit();
    const observer = new ResizeObserver(fit);
    observer.observe(el);
    return () => observer.disconnect();
  }, [pages]);

  const pointOnPage = (pageIndex: number, clientX: number, clientY: number) => {
    const surface = surfaceRefs.current[pageIndex];
    const page = pages[pageIndex];
    if (!surface || !page) return null;
    const rect = surface.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return null;
    return {
      x: ((clientX - rect.left) / rect.width) * page.width,
      y: page.height - ((clientY - rect.top) / rect.height) * page.height,
    };
  };

  useEffect(() => {
    const onMove = (event: PointerEvent) => {
      const drag = dragRef.current;
      if (!drag) return;
      const point = pointOnPage(drag.pageIndex, event.clientX, event.clientY);
      if (!point) return;
      if (drag.kind === 'text') {
        updateRuns((prev) =>
          prev.map((run) => {
            if (run.id !== drag.id) return run;
            const shift = run.baseline - (shownBaselineRef.current[run.id] ?? run.baseline);
            return { ...run, x: point.x - drag.dx, baseline: point.y - drag.dy + shift };
          })
        );
      } else if (drag.kind === 'signature') {
        updateSignatures((prev) =>
          prev.map((line) => (line.id === drag.id ? { ...line, x: point.x - drag.dx, y: point.y - drag.dy } : line))
        );
      } else {
        updateSignatures((prev) =>
          prev.map((line) => (line.id === drag.id ? { ...line, width: Math.max(36, point.x - line.x) } : line))
        );
      }
    };
    const onUp = () => {
      dragRef.current = null;
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
  }, [pages]);

  const removeRun = (id: string) => {
    const run = runsRef.current.find((item) => item.id === id);
    if (run?.erase) {
      const next = [...erasuresRef.current, { pageIndex: run.pageIndex, ...run.erase }];
      erasuresRef.current = next;
      setErasures(next);
    }
    updateRuns((prev) => prev.filter((item) => item.id !== id));
    setActive((current) => (current?.id === id ? null : current));
  };

  const removeActive = () => {
    if (!active) return;
    if (active.kind === 'text') removeRun(active.id);
    else {
      updateSignatures((prev) => prev.filter((line) => line.id !== active.id));
      setActive(null);
    }
  };

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (target?.isContentEditable || target?.tagName === 'INPUT' || target?.tagName === 'TEXTAREA') return;
      if (!active) return;
      const step = event.shiftKey ? 5 : 1;
      let dx = 0;
      let dy = 0;
      if (event.key === 'ArrowLeft') dx = -step;
      else if (event.key === 'ArrowRight') dx = step;
      else if (event.key === 'ArrowUp') dy = step;
      else if (event.key === 'ArrowDown') dy = -step;
      else if (event.key === 'Delete' || event.key === 'Backspace') {
        event.preventDefault();
        removeActive();
        return;
      } else return;
      event.preventDefault();
      if (active.kind === 'text') {
        updateRuns((prev) =>
          prev.map((run) => (run.id === active.id ? { ...run, x: run.x + dx, baseline: run.baseline + dy } : run))
        );
      } else {
        updateSignatures((prev) =>
          prev.map((line) => (line.id === active.id ? { ...line, x: line.x + dx, y: line.y + dy } : line))
        );
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [active]);

  const focusRun = (id: string) => {
    requestAnimationFrame(() => {
      document.querySelector<HTMLElement>(`[data-run="${id}"]`)?.focus();
    });
  };

  const onSurfaceClick = (pageIndex: number, event: React.MouseEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement;
    if (target.closest('.contract-pdf-run, .contract-pdf-sign')) return;
    const point = pointOnPage(pageIndex, event.clientX, event.clientY);
    if (!point) return;
    if (placingSignature) {
      const line: ContractSignatureLine = {
        id: newId(),
        pageIndex,
        x: point.x,
        y: point.y,
        width: 180,
      };
      updateSignatures((prev) => [...prev, line]);
      setActive({ kind: 'signature', id: line.id });
      setPlacingSignature(false);
      return;
    }
    const fontSize = 10;
    const pageWidth = pages[pageIndex]?.width || 612;
    const run: ContractTextRun = {
      id: newId(),
      pageIndex,
      x: point.x,
      baseline: point.y - fontSize * 0.25,
      text: '',
      fontSize,
      bold: false,
      font: 'helvetica',
      maxWidth: Math.max(72, pageWidth - point.x - 36),
      lineHeight: fontSize * 1.2,
      erase: null,
      origin: null,
    };
    updateRuns((prev) => [...prev, run]);
    setActive({ kind: 'text', id: run.id });
    focusRun(run.id);
  };

  const startTextDrag = (run: ContractTextRun, event: React.PointerEvent) => {
    event.preventDefault();
    event.stopPropagation();
    const point = pointOnPage(run.pageIndex, event.clientX, event.clientY);
    if (!point) return;
    const shown = shownBaselineRef.current[run.id] ?? run.baseline;
    dragRef.current = {
      kind: 'text',
      id: run.id,
      pageIndex: run.pageIndex,
      dx: point.x - run.x,
      dy: point.y - shown,
    };
    setActive({ kind: 'text', id: run.id });
  };

  const startSignatureDrag = (line: ContractSignatureLine, event: React.PointerEvent) => {
    event.preventDefault();
    event.stopPropagation();
    const point = pointOnPage(line.pageIndex, event.clientX, event.clientY);
    if (!point) return;
    dragRef.current = {
      kind: 'signature',
      id: line.id,
      pageIndex: line.pageIndex,
      dx: point.x - line.x,
      dy: point.y - line.y,
    };
    setActive({ kind: 'signature', id: line.id });
  };

  const startResize = (line: ContractSignatureLine, event: React.PointerEvent) => {
    event.preventDefault();
    event.stopPropagation();
    dragRef.current = { kind: 'resize', id: line.id, pageIndex: line.pageIndex };
    setActive({ kind: 'signature', id: line.id });
  };

  const activeRun = active?.kind === 'text' ? runs.find((run) => run.id === active.id) ?? null : null;

  const patchActiveRun = (patch: Partial<ContractTextRun>) => {
    if (!activeRun) return;
    updateRuns((prev) => prev.map((run) => (run.id === activeRun.id ? { ...run, ...patch } : run)));
  };

  const onRunInput = (run: ContractTextRun, event: React.FormEvent<HTMLDivElement>) => {
    const text = readBlockText(event.currentTarget);
    updateRuns((prev) => prev.map((item) => (item.id === run.id ? { ...item, text } : item)));
  };

  const onRunKeyDown = (_run: ContractTextRun, event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    insertAtCaret(event.currentTarget, '\n');
    const text = readBlockText(event.currentTarget);
    updateRuns((prev) => prev.map((item) => (item.id === _run.id ? { ...item, text } : item)));
  };

  const onRunBlur = (run: ContractTextRun, event: React.FocusEvent<HTMLDivElement>) => {
    const text = readBlockText(event.currentTarget).trim();
    if (!text) removeRun(run.id);
    else updateRuns((prev) => prev.map((item) => (item.id === run.id ? { ...item, text } : item)));
  };

  const onPaste = (event: React.ClipboardEvent<HTMLDivElement>) => {
    event.preventDefault();
    const text = event.clipboardData.getData('text/plain').replace(/\r\n/g, '\n').replace(/\r/g, '\n');
    if (!text) return;
    insertAtCaret(event.currentTarget, text);
    const value = readBlockText(event.currentTarget);
    const id = event.currentTarget.dataset.run;
    if (!id) return;
    updateRuns((prev) => prev.map((item) => (item.id === id ? { ...item, text: value } : item)));
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const liveText = new Map<string, string>();
      document.querySelectorAll<HTMLElement>('[data-run]').forEach((node) => {
        const id = node.dataset.run;
        if (id) liveText.set(id, readBlockText(node).trim());
      });
      const kept: ContractTextRun[] = [];
      const removed = [...erasuresRef.current];
      for (const run of runsRef.current) {
        if (liveText.has(run.id)) run.text = liveText.get(run.id) ?? run.text;
        const text = run.text.trim();
        if (!text) {
          if (run.erase) removed.push({ pageIndex: run.pageIndex, ...run.erase });
          continue;
        }
        kept.push({ ...run, text });
      }
      const placed = layoutContractBaselines(kept, (run, sample) =>
        measureWidth(sample, run.fontSize, run.bold, run.font)
      );
      const baselineById: Record<string, number> = {};
      for (const placement of placed) baselineById[placement.id] = placement.baseline;
      const model: ContractPdfEditModel = {
        runs: kept,
        signatures: signaturesRef.current,
        erasures: removed,
      };
      const drawModel: ContractPdfEditModel = {
        ...model,
        runs: kept.map((run) => ({ ...run, baseline: baselineById[run.id] ?? run.baseline })),
      };
      const bytes = await applyContractPdfEdits(pdfBytes, drawModel, rules);
      onSave(bytes, model);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save the PDF.');
    } finally {
      setSaving(false);
    }
  };

  const runStyle = (run: ContractTextRun, page: PageSize): React.CSSProperties => {
    const shown = shownBaselines[run.id] ?? run.baseline;
    const leading = run.lineHeight > 0 ? run.lineHeight : run.fontSize * 1.2;
    const maxWidth = run.maxWidth > 20 ? run.maxWidth : Math.max(72, page.width - run.x - 36);
    const fontFamily = fontStack(run.font);
    const fontSize = run.fontSize * viewScale;
    const lineHeight = leading * viewScale;
    return {
      left: run.x * viewScale,
      top: (page.height - shown) * viewScale - cssBaselineOffset(fontFamily, run.bold, fontSize, lineHeight),
      width: maxWidth * viewScale,
      fontSize,
      lineHeight: `${lineHeight}px`,
      fontWeight: run.bold ? 700 : 400,
      fontFamily,
    };
  };

  return (
    <div className="contract-pdf-editor">
      <div className="contract-pdf-editor-toolbar">
        <button type="button" className="contract-pdf-editor-back" onClick={onCancel} disabled={saving}>
          <EditorIcon name="back" />
          Back
        </button>
        <span className="contract-pdf-editor-divider" />
        <button
          type="button"
          className={`contract-pdf-icon-btn${placingSignature ? ' is-on' : ''}`}
          title={placingSignature ? 'Click the page to place the signature line' : 'Signature line'}
          aria-label="Signature line"
          aria-pressed={placingSignature}
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => setPlacingSignature((on) => !on)}
        >
          <EditorIcon name="signature" />
        </button>
        <button
          type="button"
          className={`contract-pdf-icon-btn${activeRun?.bold ? ' is-on' : ''}`}
          title="Bold"
          aria-label="Bold"
          aria-pressed={Boolean(activeRun?.bold)}
          disabled={!activeRun}
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => activeRun && patchActiveRun({ bold: !activeRun.bold })}
        >
          <EditorIcon name="bold" />
        </button>
        <button
          type="button"
          className="contract-pdf-icon-btn"
          title="Smaller"
          aria-label="Smaller text"
          disabled={!activeRun}
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => activeRun && patchActiveRun({ fontSize: Math.max(6, Math.round((activeRun.fontSize - 1) * 2) / 2) })}
        >
          <EditorIcon name="smaller" />
        </button>
        <button
          type="button"
          className="contract-pdf-icon-btn"
          title="Larger"
          aria-label="Larger text"
          disabled={!activeRun}
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => activeRun && patchActiveRun({ fontSize: Math.min(28, Math.round((activeRun.fontSize + 1) * 2) / 2) })}
        >
          <EditorIcon name="larger" />
        </button>
        <span className="contract-pdf-editor-size">{activeRun ? `${activeRun.fontSize} pt` : ''}</span>
        <button
          type="button"
          className="contract-pdf-icon-btn"
          title="Delete"
          aria-label="Delete"
          disabled={!active}
          onMouseDown={(event) => event.preventDefault()}
          onClick={removeActive}
        >
          <EditorIcon name="delete" />
        </button>
        <button type="button" className="contract-pdf-editor-save" onClick={handleSave} disabled={saving || loading}>
          <EditorIcon name="save" />
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
      {error ? (
        <p className="create-order-error" role="alert">
          {error}
        </p>
      ) : null}
      {loading ? <p className="contract-form-section-desc">Opening the contract…</p> : null}
      <div className="contract-pdf-editor-pages" ref={pagesScrollRef}>
        {pages.map((page, pageIndex) => (
          <div key={pageIndex} className="contract-pdf-editor-page">
            <p className="contract-pdf-editor-page-label">Page {pageIndex + 1}</p>
            <div
              ref={(node) => {
                surfaceRefs.current[pageIndex] = node;
              }}
              className={`contract-pdf-editor-surface${placingSignature ? ' is-placing' : ''}`}
              style={{ width: page.width * viewScale, height: page.height * viewScale }}
              onClick={(event) => onSurfaceClick(pageIndex, event)}
            >
              <canvas
                ref={(node) => {
                  canvasRefs.current[pageIndex] = node;
                }}
              />
              {runs
                .filter((run) => run.pageIndex === pageIndex)
                .map((run) => (
                  <div
                    key={run.id}
                    className={`contract-pdf-run${active?.id === run.id ? ' is-selected' : ''}`}
                    style={runStyle(run, page)}
                  >
                    <button
                      type="button"
                      className="contract-pdf-grip"
                      aria-label="Drag to move"
                      onPointerDown={(event) => startTextDrag(run, event)}
                    />
                    <div
                      data-run={run.id}
                      contentEditable
                      suppressContentEditableWarning
                      spellCheck
                      role="textbox"
                      aria-label="Contract text"
                      ref={(node) => {
                        if (!node || document.activeElement === node) return;
                        if (readBlockText(node) !== run.text) node.textContent = run.text;
                      }}
                      onFocus={() => setActive({ kind: 'text', id: run.id })}
                      onInput={(event) => onRunInput(run, event)}
                      onKeyDown={(event) => onRunKeyDown(run, event)}
                      onBlur={(event) => onRunBlur(run, event)}
                      onPaste={onPaste}
                    />
                  </div>
                ))}
              {signatures
                .filter((line) => line.pageIndex === pageIndex)
                .map((line) => (
                  <div
                    key={line.id}
                    className={`contract-pdf-sign${active?.id === line.id ? ' is-selected' : ''}`}
                    style={{
                      left: line.x * viewScale,
                      top: (page.height - line.y) * viewScale,
                      width: line.width * viewScale,
                    }}
                    onPointerDown={(event) => startSignatureDrag(line, event)}
                  >
                    <span>Signature</span>
                    <button
                      type="button"
                      className="contract-pdf-sign-resize"
                      aria-label="Resize signature line"
                      onPointerDown={(event) => startResize(line, event)}
                    />
                  </div>
                ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

function EditorIcon({ name }: { name: 'back' | 'signature' | 'bold' | 'smaller' | 'larger' | 'delete' | 'save' }) {
  const common = { viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.8, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };
  if (name === 'back') {
    return (
      <svg {...common} aria-hidden="true">
        <path d="M15 6l-6 6 6 6" />
      </svg>
    );
  }
  if (name === 'signature') {
    return (
      <svg {...common} aria-hidden="true">
        <path d="M4 17h16" />
        <path d="M7 17c1.2-4 2-6 3.2-6 1.4 0 .8 6 2.4 6 1.2 0 1.6-3 2.6-5.2C16 10 16.6 9 18 9" />
      </svg>
    );
  }
  if (name === 'bold') {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <text x="6" y="17" fontSize="15" fontWeight="700" fill="currentColor" fontFamily="Arial, sans-serif">B</text>
      </svg>
    );
  }
  if (name === 'smaller') {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <text x="3" y="16" fontSize="12" fontWeight="600" fill="currentColor" fontFamily="Arial, sans-serif">A</text>
        <text x="13" y="17" fontSize="14" fontWeight="700" fill="currentColor" fontFamily="Arial, sans-serif">−</text>
      </svg>
    );
  }
  if (name === 'larger') {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <text x="2" y="17" fontSize="15" fontWeight="700" fill="currentColor" fontFamily="Arial, sans-serif">A</text>
        <text x="14" y="16" fontSize="14" fontWeight="700" fill="currentColor" fontFamily="Arial, sans-serif">+</text>
      </svg>
    );
  }
  if (name === 'delete') {
    return (
      <svg {...common} aria-hidden="true">
        <path d="M5 7h14" />
        <path d="M9 7V5h6v2" />
        <path d="M8 7l1 12h6l1-12" />
      </svg>
    );
  }
  return (
    <svg {...common} aria-hidden="true">
      <path d="M6 12.5l4 4 8-9" />
    </svg>
  );
}

export default ContractPdfEditor;
